import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { templates } from '@repletoryreach/sdk-api';
import { Stack, Card, Skeleton, Text } from '@repletoryreach/sdk-ui';
import { Alert, Tabs, BreadcrumbTrail } from '@repletoryreach/sdk-ui/data-display';
import { OverviewTab } from './components/OverviewTab';
import { VariablesTab } from './components/VariablesTab';
import { ComponentsTab } from './components/ComponentsTab';
import { LifecycleTab } from './components/LifecycleTab';
import { useT } from '../../../i18n/I18nProvider';
import { useTemplateRealtimeSync } from '../../../data/useTemplateEvent';
import { formatDateTime } from '../../../lib/format';
import { DetailHeader, StatusPill, BackIcon, type PillTone } from '../../../components/panel';
import '../templates.css';

/** Tab ids, in the order they render. */
const TABS = ['overview', 'variables', 'components', 'lifecycle'] as const;
type TabId = (typeof TABS)[number];

function statusTone(s: string): PillTone {
  switch (s) {
    case 'approved': return 'approved';
    case 'submitted': return 'submitted';
    case 'rejected': return 'rejected';
    case 'paused': return 'paused';
    default: return 'draft';
  }
}

export default function TemplateViewPage() {
  const t = useT();

  /*
   * §4 item 1: this page's whole job for a `submitted` template is watching
   * for Meta's verdict, and it had no `refetchInterval` and no subscription,
   * so the verdict never arrived. The fanout has worked end to end since W4b
   * and only admin-ui consumed it.
   */
  useTemplateRealtimeSync();
  const { id } = useParams();

  /*
   * The tab lives in the URL. It was uncontrolled, so there was no way to link
   * anyone to Lifecycle, and a reload always dropped them back on Overview:
   * the tab a toast about Meta's verdict most wants to open.
   */
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: TabId = (TABS as readonly string[]).includes(raw ?? '') ? (raw as TabId) : 'overview';
  const setTab = (next: string) => setParams((prev) => {
    const q = new URLSearchParams(prev);
    q.set('tab', next);
    return q;
  }, { replace: true });

  const { data, isLoading, error } = useQuery({
    queryKey: ['template', id],
    queryFn: () => templates.get(id!),
    enabled: !!id
  });

  if (isLoading) return <TemplateSkeleton />;
  if (error) return <Alert color="error" role="alert">{(error as Error).message}</Alert>;
  if (!data?.data) {
    return (
      <Alert color="warning">
        <Stack gap={8}>
          <Text role="bodySmall">{t('templates.view.notFound')}</Text>
          <Link to="/templates" className="rr-detailhead__back">
            <BackIcon /> {t('templates.view.backToAll')}
          </Link>
        </Stack>
      </Alert>
    );
  }
  const tpl = data.data;

  return (
    <Stack gap="md">
      <BreadcrumbTrail
        items={[{ label: t('templates.title'), href: '/templates' }, { label: tpl.code }]}
        renderLink={(href, label) => <Link to={href}>{label}</Link>}
      />

      <Card withBorder>
        <DetailHeader
          icon={<TemplateIcon />}
          tint={TINT[tpl.status]}
          title={tpl.code}
          mono
          pill={<StatusPill tone={statusTone(tpl.status)}>{t(`templates.status.${tpl.status}`, tpl.status)}</StatusPill>}
          description={
            <>
              {t(`templates.category.${tpl.category}`, tpl.category ?? '')}
              {' · '}
              {tpl.language}
            </>
          }
          actions={
            // Created and updated belong in the header, not buried in a tab:
            // "when did this last change?" is the question a template in review
            // raises, and it was three clicks away.
            <div className="rr-templates__stamps">
              <Stamp label={t('common.created')} value={formatDateTime(tpl.created_at)} icon={<CalendarIcon />} />
              <Stamp label={t('common.updated')} value={formatDateTime(tpl.updated_at)} icon={<ClockIcon />} />
            </div>
          }
          back={
            <Link to="/templates" className="rr-detailhead__back">
              <BackIcon /> {t('templates.view.backToAll')}
            </Link>
          }
        />
      </Card>

      <Tabs value={tab} onChange={setTab}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<EyeIcon />}>{t('common.overview')}</Tabs.Tab>
          <Tabs.Tab value="variables" leftSection={<BracesIcon />}>{t('templates.view.tabs.variables')}</Tabs.Tab>
          <Tabs.Tab value="components" leftSection={<CodeIcon />}>{t('templates.view.tabs.components')}</Tabs.Tab>
          <Tabs.Tab value="lifecycle" leftSection={<ClockIcon />}>{t('broadcasts.view.tabs.lifecycle')}</Tabs.Tab>
        </Tabs.List>

        {/*
          Overview and Variables sit side by side, as in the reference: the
          preview answers "what will they see?" and the variables answer "with
          what in it?", and reading one without the other is half the answer.
        */}
        <Tabs.Panel value="overview">
          <div className="rr-templates__grid">
            <OverviewTab template={tpl} />
            <VariablesTab template={tpl} />
          </div>
        </Tabs.Panel>
        <Tabs.Panel value="variables">
          <div style={{ paddingTop: 16 }}><VariablesTab template={tpl} /></div>
        </Tabs.Panel>
        <Tabs.Panel value="components">
          <div className="rr-templates__grid">
            <ComponentsTab components={tpl.components} />
            <LifecycleTab template={tpl} />
          </div>
        </Tabs.Panel>
        <Tabs.Panel value="lifecycle">
          <div style={{ paddingTop: 16 }}><LifecycleTab template={tpl} /></div>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

/** Tint for the header icon, so status is legible before the pill is read. */
const TINT: Record<string, string> = {
  approved: 'var(--rr-color-success, #16a34a)',
  submitted: 'var(--rr-color-warning, #f59e0b)',
  rejected: 'var(--rr-color-error, #dc2626)',
  paused: 'var(--rr-color-warning, #f59e0b)'
};

function Stamp({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <div className="rr-templates__stamp">
      <span className="rr-templates__stamp-label">{label}</span>
      <span className="rr-templates__stamp-value">{icon}{value}</span>
    </div>
  );
}

/** Shaped like the page it stands in for, rather than one grey block. */
function TemplateSkeleton() {
  return (
    <Stack gap="md">
      <Skeleton height={18} width={220} />
      <Card withBorder>
        <div className="rr-detailhead">
          <Skeleton height={56} width={56} radius="lg" />
          <div className="rr-detailhead__main">
            <Skeleton height={24} width="45%" />
            <Skeleton height={14} width="30%" />
          </div>
        </div>
      </Card>
      <Skeleton height={38} />
      <div className="rr-templates__grid">
        <Skeleton height={320} />
        <Skeleton height={320} />
      </div>
    </Stack>
  );
}

function TemplateIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 20 20" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 2.4h7.4L16 6v11.6H5z" />
      <path d="M12.2 2.5v3.7h3.7" />
      <path d="M7.6 10.2h6M7.6 13.3h4" />
    </svg>
  );
}
function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M1.6 8S3.9 3.8 8 3.8 14.4 8 14.4 8 12.1 12.2 8 12.2 1.6 8 1.6 8z" />
      <circle cx="8" cy="8" r="2" />
    </svg>
  );
}
function BracesIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6 2.5c-1.4 0-1.6.9-1.6 2.1s0 2.2-1.4 2.2v2.4c1.4 0 1.4 1 1.4 2.2s.2 2.1 1.6 2.1" />
      <path d="M10 2.5c1.4 0 1.6.9 1.6 2.1s0 2.2 1.4 2.2v2.4c-1.4 0-1.4 1-1.4 2.2s-.2 2.1-1.6 2.1" />
    </svg>
  );
}
function CodeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="5.4,4.6 2,8 5.4,11.4" />
      <polyline points="10.6,4.6 14,8 10.6,11.4" />
    </svg>
  );
}
function ClockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="8" cy="8" r="6.2" />
      <polyline points="8,4.4 8,8 10.6,9.4" />
    </svg>
  );
}
function CalendarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2.2" y="3.4" width="11.6" height="10.4" rx="2" />
      <path d="M2.2 6.6h11.6M5.4 1.9v2.6M10.6 1.9v2.6" />
    </svg>
  );
}
